const BASE = '/api/v1';

export class APIError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message || `API ${status}`);
    this.status = status;
    this.body = body;
  }
}

// `keepalive` lets a request outlive the page: iOS suspends a backgrounded PWA
// without warning, and an ordinary fetch fired from the visibilitychange flush
// dies with the tab, taking the edit with it. The spec caps the *total* body of
// all in-flight keepalive requests at 64 KB and fails anything over it, so this
// is not safe to switch on globally — the supplement scan and photo-attach
// uploads go through request() with a File in a FormData body and would blow
// straight past the cap. It is therefore limited to string (JSON) bodies under
// this many UTF-16 chars; a JS string encodes to at most 3 UTF-8 bytes per
// char, so 16384 can never exceed 48 KB. Every real write here is two orders of
// magnitude smaller (the largest is a day PATCH carrying the notes textarea);
// anything bigger simply falls back to a plain fetch, which is what it was.
const KEEPALIVE_MAX_BODY_CHARS = 16384;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // For FormData bodies the browser must own the Content-Type so it can
  // include the multipart boundary — explicitly setting application/json
  // would break the upload. Detect and skip the default header.
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const defaultHeaders: Record<string, string> = isFormData
    ? {}
    : { 'Content-Type': 'application/json' };
  const merged: Record<string, string> = {
    ...defaultHeaders,
    ...(init?.headers as Record<string, string> | undefined),
  };
  // Defense in depth: even if a caller spreads its own Content-Type for a
  // FormData body (e.g. cargo-culted from a JSON example), strip it so the
  // browser-set boundary survives.
  if (isFormData && 'Content-Type' in merged) {
    delete merged['Content-Type'];
  }
  // A bodyless request (GET/DELETE) costs nothing against the quota and still
  // benefits, so it keeps the flag too.
  const keepalive =
    typeof init?.body === 'string'
      ? init.body.length <= KEEPALIVE_MAX_BODY_CHARS
      : init?.body === undefined || init?.body === null;
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    keepalive,
    ...init,
    headers: merged,
  });
  if (!res.ok) {
    // Read body once as text — calling res.json() then res.text() throws
    // "Body is disturbed or locked" because the stream can only be consumed once.
    const raw = await res.text();
    let body: unknown = raw;
    try {
      body = JSON.parse(raw);
    } catch {
      // keep raw text — server returned non-JSON (e.g. HTML 502 from a misrouted proxy)
    }
    throw new APIError(res.status, body);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

// --- types ---

export type User = { id: number; email: string; name: string };

// Body for POST /bet/init. Every field is optional — empty body falls back
// to the legacy default bet (Gio's 96→72 kg over 2026-05-04 → 2026-12-31).
// Multi-tenant onboarding sends all fields the wizard collects.
export type InitBetBody = {
  starting_weight_kg?: number;
  starting_date?: string;       // YYYY-MM-DD, must be a Monday for clean week alignment
  deadline_date?: string;       // YYYY-MM-DD
  target_weight_kg?: number;
  buffer_target_kg?: number;
  buffer_target_date?: string;
  height_cm?: number;
  stakes_usd?: number;
};

export type Bet = {
  id: number;
  starting_weight_kg: number;
  starting_date: string;
  deadline_date: string;
  target_weight_kg: number;
  buffer_target_kg: number | null;
  buffer_target_date: string | null;
  height_cm: number;
  stakes_usd: number | null;
  committed_at: string | null;
  recalibrated_at: string | null;
  creatine_load_started_at: string | null;
  created_at: string;
  days_to_deadline: number;
};

// WeeklyMilestone is one row of the per-bet weight trajectory (weekly_targets
// table). Returned in week-ascending order from GET /bet/milestones.
export type WeeklyMilestone = {
  week_number: number;
  monday_date: string;
  target_weight_kg: number;
  block: 'A' | 'B' | 'C';
};

export type MealEntry = {
  meal_id: 'breakfast' | 'lunch' | 'dinner';
  completed: boolean;
  completed_at: string | null;
  deviation_notes: string | null;
};

export type OffPlanFoodEntry = {
  id: number;
  description: string;
  est_kcal: number;
  protein_g: number | null;
  fat_g: number | null;
  carbs_g: number | null;
  created_at: string;
};

export type FoodEstimate = {
  description: string;
  kcal: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
};

export type FoodEstimateResponse = {
  items: FoodEstimate[];
};

export type MacroPair = {
  consumed: number;
  target: number;
};
export type DailyMacros = {
  kcal: MacroPair;
  protein: MacroPair;
  fat: MacroPair;
  carbs: MacroPair;
};

export type DailyRangeEntry = {
  date: string;
  weight_kg: number | null;
  steps: number;
  off_plan_kcal_total: number;
  macros: DailyMacros;
};

export type DailyLog = {
  id: number;
  log_date: string;
  weight_kg: number | null;
  waist_cm: number | null;
  steps: number;
  sleep_hours: number | null;
  sleep_quality_1to10: number | null;
  energy_1to10: number | null;
  hunger_1to10: number | null;
  training_completed: boolean;
  training_session_id: string | null;
  mobility_completed: boolean;
  creatine_taken: boolean;
  magnesium_taken: boolean;
  fish_oil_taken: boolean;
  d3_taken: boolean;
  tongkat_taken: boolean;
  fadogia_taken: boolean;
  alcohol_consumed: boolean;
  water_liters: number | null;
  salt_grams: number | null;
  notes: string | null;
  locked: boolean;
  meals: MealEntry[];
  off_plan_food: OffPlanFoodEntry[];
  off_plan_kcal_total: number;
  macros: DailyMacros;
};

export type Ingredient = { item: string; amount: string };
export type Meal = {
  id: 'breakfast' | 'lunch' | 'dinner';
  label: string;
  scheduled_time: string;
  calories: number;
  protein_g?: number;
  fat_g?: number;
  carbs_g?: number;
  ingredients: Ingredient[];
  prep_notes?: string;
};
export type DailyTotals = { calories: number; protein_g: number; fat_g: number; carbs_g: number };
export type MealTemplate = {
  block: 'A' | 'B' | 'C';
  description?: string;
  meals?: Meal[];
  daily_totals?: DailyTotals;
  adjustments?: string;
  flexibility?: string;
  step_target: number;
};
export type MealsByDate = { date: string; block: 'A' | 'B' | 'C'; meals: MealTemplate };

export type Exercise = {
  name: string;
  sets: number;
  reps: string;
  load?: string;
  rest_sec?: number;
  tempo_note?: string;
  video_url?: string;
  timer_sec?: number;
  work_sec?: number;
  rest_int_sec?: number;
};
export type TrainingSession = {
  id: string;
  phase: string;
  day_of_week: string;
  type: string;
  title: string;
  equipment?: string[];
  exercises?: Exercise[];
  warmup_notes?: string;
  notes?: string;
};
export type TodayTraining = {
  date: string;
  week_number: number;
  training_week_number?: number; // Independent reset; absent on older servers.
  block: 'A' | 'B' | 'C';
  session: TrainingSession;
  meals: MealTemplate;
};

export type WeekDay = {
  day_of_week: string;
  date: string;
  is_today: boolean;
  session: TrainingSession;
};
export type TrainingWeek = {
  week_number: number;
  training_week_number?: number;
  block: 'A' | 'B' | 'C';
  days: WeekDay[];
};

// Supplement system — see /api/v1/supplements/*
export type SupplementSchedule = {
  id?: number;
  hour: number;
  minute: number;
  enabled?: boolean;
};

export type SupplementDose = {
  id: number;
  date: string;
  scheduled_time: string;
  status: 'pending' | 'taken' | 'skipped';
  taken_at: string | null;
};

export type Supplement = {
  id: number;
  name: string;
  dose: string;
  info_label: string;
  coach_summary: string;
  tags: string[];
  legacy_column?: string;
  photo_url?: string | null;
  schedules: SupplementSchedule[];
  todays_doses: SupplementDose[];
};

export type AddSupplementBody = {
  name: string;
  dose: string;
  info_label?: string;
  coach_summary?: string;
  tags?: string[];
  scan_id?: number;
  schedules: { hour: number; minute: number }[];
};

// SupplementScan — parsed output of POST /supplements/scan. scan_id refers
// to an orphan supplement_photos row that lives for ~1h and is claimed by
// passing it back as scan_id in the subsequent POST /supplements body.
export type SupplementScan = {
  scan_id: number;
  name: string;
  dose: string;
  info_label: string;
  coach_summary: string;
  tags: string[];
};

export type PatchSupplementBody = {
  name?: string;
  dose?: string;
  info_label?: string;
  schedules?: { hour: number; minute: number }[];
};

// BulkSupplementTimeBody — POST /supplements/bulk-schedule. Every listed
// supplement's schedule is REPLACED by this single time, so the caller must
// have told the user that first. Returns the caller's full supplement list.
export type BulkSupplementTimeBody = {
  supplement_ids: number[];
  hour: number;
  minute: number;
};

export type SupplementExplanation = {
  info_label: string;
  coach_summary: string;
  tags: string[];
};

export type RangeSessionLite = {
  id: string;
  type: string;
  title: string;
};
export type RangeDay = {
  date: string;
  weekday: number; // 0=Sun..6=Sat
  week_number: number; // 0 if pre-bet
  block: 'A' | 'B' | 'C';
  session: RangeSessionLite;
};

export type DailyLogPatch = Partial<{
  weight_kg: number;
  waist_cm: number;
  steps: number;
  sleep_hours: number;
  sleep_quality_1to10: number;
  energy_1to10: number;
  hunger_1to10: number;
  training_completed: boolean;
  training_session_id: string;
  mobility_completed: boolean;
  creatine_taken: boolean;
  magnesium_taken: boolean;
  fish_oil_taken: boolean;
  d3_taken: boolean;
  tongkat_taken: boolean;
  fadogia_taken: boolean;
  alcohol_consumed: boolean;
  water_liters: number;
  salt_grams: number;
  notes: string;
}>;

// --- settings, notification preferences, custom reminders ---

export type UserSettings = {
  user_id: number;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;  // 'HH:MM' user-local
  quiet_hours_end?: string;
  units: 'metric' | 'imperial';
  theme: 'dark' | 'light' | 'system';
  week_start: 'monday' | 'sunday';
  snooze_default_minutes: number;
  eating_window_start: string; // 'HH:MM' user-local, default 09:00
  eating_window_end: string;   // 'HH:MM' user-local, default 18:00
};

export type UserSettingsPatch = Partial<Omit<UserSettings, 'user_id'>>;

export type NotificationPreferenceOverride = {
  enabled: boolean;
  custom_hour?: number;
  custom_minute?: number;
  custom_title?: string;
  custom_body?: string;
};

// One row in the notification catalog. Mirrors handlers/settings.go::prefRow.
// `weekday` is -1 for daily/trigger types; for weekly it's a Go time.Weekday
// (Sun=0..Sat=6). `has_fixed_time` is false for context-driven triggers.
export type NotificationPreferenceRow = {
  type: string;
  group: 'daily' | 'weekly' | 'trigger';
  label: string;
  has_fixed_time: boolean;
  weekday: number;
  default_hour: number;
  default_min: number;
  default_title: string;
  default_body: string;
  description?: string;
  override?: NotificationPreferenceOverride;
};

export type ReminderFreq = 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
export type ReminderEndType = 'never' | 'on_date' | 'after_count';

// Mirrors notifications.Reminder JSON. days_mask uses Go time.Weekday bit
// indexing: bit 0=Sun ... bit 6=Sat. monthly_day is 1-31 (NULL = day-of-month
// from start_date).
export type UserReminder = {
  id: number;
  user_id: number;
  label: string;
  body: string;
  hour: number;
  minute: number;
  freq: ReminderFreq;
  interval_n: number;
  days_mask?: number;
  monthly_day?: number;
  start_date: string;       // YYYY-MM-DD
  end_type: ReminderEndType;
  end_date?: string;
  end_count?: number;
  enabled: boolean;
  fired_count: number;
  next_fire_at?: string;    // ISO8601 UTC
  last_fired_at?: string;
};

export type CreateUserReminder = Omit<UserReminder, 'id' | 'user_id' | 'fired_count' | 'next_fire_at' | 'last_fired_at'>;

// --- api surface ---

export const api = {
  // Auth — primary surface is email + password (register/login). Magic-link
  // endpoints stay wired for future password-reset use but aren't surfaced
  // in the current Login UI.
  register: (body: { email: string; password: string; name: string }) =>
    request<{ user: User; session_token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  login: (body: { email: string; password: string }) =>
    request<{ user: User; session_token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  requestMagicLink: (email: string) =>
    request<{ ok: boolean }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  verifyMagicLink: (token: string) =>
    request<{ user: User; session_token: string }>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  me: () => request<User>('/auth/me'),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  changePassword: (body: { current_password: string; new_password: string }) =>
    request<{ user: User; session_token: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Bet
  getBet: () => request<Bet>('/bet'),
  initBet: (body?: InitBetBody) =>
    request<Bet>('/bet/init', {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  recalibrateBet: (startingWeightKg: number) =>
    request<Bet>('/bet/recalibrate', {
      method: 'POST',
      body: JSON.stringify({ starting_weight_kg: startingWeightKg }),
    }),
  getMilestones: () => request<{ milestones: WeeklyMilestone[] }>('/bet/milestones'),

  // Daily logs
  getToday: () => request<DailyLog>('/logs/daily/today'),
  getDay: (date: string) => request<DailyLog>(`/logs/daily/${date}`),
  patchDay: (date: string, patch: DailyLogPatch) =>
    request<DailyLog>(`/logs/daily/${date}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  setWeight: (weight_kg: number) =>
    request<DailyLog>('/logs/daily/today/weight', {
      method: 'POST',
      body: JSON.stringify({ weight_kg }),
    }),
  setMeal: (meal_id: MealEntry['meal_id'], completed: boolean) =>
    request<DailyLog>('/logs/daily/today/meal', {
      method: 'POST',
      body: JSON.stringify({ meal_id, completed }),
    }),
  addOffPlanFood: (
    date: string,
    description: string,
    est_kcal: number,
    macros?: { protein_g: number; fat_g: number; carbs_g: number },
  ) =>
    request<{ id: number; day: DailyLog }>(`/logs/daily/${date}/off-plan`, {
      method: 'POST',
      body: JSON.stringify({ description, est_kcal, ...(macros ?? {}) }),
    }),
  deleteOffPlanFood: (date: string, id: number) =>
    request<DailyLog>(`/logs/daily/${date}/off-plan/${id}`, { method: 'DELETE' }),
  getDailyRange: (from: string, to: string) =>
    request<DailyRangeEntry[]>(
      `/logs/daily/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),

  estimateFood: (description: string) =>
    request<FoodEstimateResponse>('/foods/estimate', {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),

  // Workout timers — schedules a one-shot push for `seconds` from now. The
  // backend delivers it if the app is asleep by then (a locked iPhone suspends
  // our JS, so the in-app cue cannot fire); the client tracks the same expiry
  // locally for the countdown UI and cancels this push if it's still awake to
  // sound the cue itself. Backs the rest, hold, and interval timers — `title`
  // defaults to "Rest done" when omitted.
  startRest: (seconds: number, label: string, title?: string) =>
    request<{ id: number; expires_at: string }>('/training/rest-start', {
      method: 'POST',
      body: JSON.stringify({ seconds, label, title }),
    }),
  cancelRest: (id: number) =>
    request<void>('/training/rest-cancel', {
      method: 'POST',
      body: JSON.stringify({ id }),
    }),

  // Persisted per-set checkboxes. listTrainingSets hydrates the workout
  // view on reload; toggleTrainingSet upserts/deletes a single row.
  listTrainingSets: (date: string, sessionId: string) =>
    request<{ exercise_index: number; set_index: number }[]>(
      `/training/sets?date=${encodeURIComponent(date)}&session_id=${encodeURIComponent(sessionId)}`,
    ),
  toggleTrainingSet: (
    date: string,
    sessionId: string,
    exerciseIndex: number,
    setIndex: number,
    completed: boolean,
  ) =>
    request<void>('/training/sets/toggle', {
      method: 'POST',
      body: JSON.stringify({
        date,
        session_id: sessionId,
        exercise_index: exerciseIndex,
        set_index: setIndex,
        completed,
      }),
    }),
  // Calendar's "workout done" markers — one cheap call per visible range.
  listTrainingCompletions: (from: string, to: string) =>
    request<{ dates: string[] }>(
      `/training/completions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),

  // Templates
  getMealTemplate: (block: 'A' | 'B' | 'C') => request<MealTemplate>(`/templates/meals/${block}`),
  getMealsByDate: (date: string) => request<MealsByDate>(`/templates/meals/by-date/${date}`),
  getTodayTraining: () => request<TodayTraining>('/templates/training/today'),
  getTrainingWeek: () => request<TrainingWeek>('/templates/training/week'),
  getTrainingRange: (from: string, to: string) =>
    request<RangeDay[]>(
      `/templates/training/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  getTrainingSession: (id: string) => request<TrainingSession>(`/templates/training/${id}`),
  patchTrainingSession: (id: string, session: TrainingSession) =>
    request<TrainingSession>(`/templates/training/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(session),
    }),
  resetTrainingSession: (id: string) =>
    request<TrainingSession>(`/templates/training/${id}/override`, { method: 'DELETE' }),

  // Supplements
  getSupplements: () => request<Supplement[]>('/supplements'),
  addSupplement: (body: AddSupplementBody) =>
    request<Supplement>('/supplements', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateSupplement: (id: number, body: PatchSupplementBody) =>
    request<Supplement>(`/supplements/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  bulkSetSupplementTime: (body: BulkSupplementTimeBody) =>
    request<Supplement[]>('/supplements/bulk-schedule', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteSupplement: (id: number) =>
    request<void>(`/supplements/${id}`, { method: 'DELETE' }),
  takeDose: (doseId: number) =>
    request<Supplement>(`/supplements/doses/${doseId}/take`, { method: 'POST' }),
  skipDose: (doseId: number) =>
    request<Supplement>(`/supplements/doses/${doseId}/skip`, { method: 'POST' }),
  explainSupplement: (id: number) =>
    request<SupplementExplanation>(`/supplements/${id}/explain`, { method: 'POST' }),
  scanSupplement: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<SupplementScan>('/supplements/scan', {
      method: 'POST',
      body: form,
    });
  },
  attachSupplementPhoto: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<Supplement>(`/supplements/${id}/photo`, {
      method: 'POST',
      body: form,
    });
  },

  // Push
  testPush: () => request<{ sent: number }>('/notifications/test', { method: 'POST' }),

  // Dashboard
  getDashboard: () => request<DashboardPayload>('/bet/dashboard'),

  // Reviews
  getCurrentReview: () => request<WeeklyReview>('/reviews/current'),
  getReviewByWeek: (week: number) => request<WeeklyReview>(`/reviews/${week}`),
  runReview: (week: number) =>
    request<WeeklyReview>(`/reviews/${week}/run`, { method: 'POST' }),
  ackReview: (week: number) =>
    request<{ ok: boolean }>(`/reviews/${week}/acknowledge`, { method: 'POST' }),
  patchReviewNotes: (week: number, user_notes: string) =>
    request<{ ok: boolean; user_notes: string }>(`/reviews/${week}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ user_notes }),
    }),
  listReviews: () => request<WeeklyReview[]>('/reviews/history'),

  // Insurance
  getInsurance: () =>
    request<{ items: InsuranceItem[]; probability_ladder: ProbabilityLadderRow[] }>('/insurance'),
  patchInsurance: (id: string, body: { active?: boolean; notes?: string }) =>
    request<void>(`/insurance/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Photos
  listPhotos: (date?: string) =>
    request<PhotoMeta[]>(`/photos${date ? `?date=${date}` : ''}`),
  deletePhoto: (id: number) =>
    request<void>(`/photos/${id}`, { method: 'DELETE' }),

  // User settings
  getUserSettings: () => request<UserSettings>('/user/settings'),
  patchUserSettings: (patch: UserSettingsPatch) =>
    request<UserSettings>('/user/settings', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // Notification preferences (catalog + per-user overrides)
  listNotificationPreferences: () =>
    request<{ preferences: NotificationPreferenceRow[] }>('/user/notification-preferences'),
  upsertNotificationPreference: (
    type: string,
    body: Partial<NotificationPreferenceOverride>,
  ) =>
    request<void>(`/user/notification-preferences/${encodeURIComponent(type)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  resetNotificationPreference: (type: string) =>
    request<void>(`/user/notification-preferences/${encodeURIComponent(type)}`, {
      method: 'DELETE',
    }),

  // Custom user reminders (iPhone-alarm-style)
  listUserReminders: () =>
    request<{ reminders: UserReminder[] }>('/user/reminders'),
  createUserReminder: (body: CreateUserReminder) =>
    request<UserReminder>('/user/reminders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateUserReminder: (id: number, body: CreateUserReminder) =>
    request<UserReminder>(`/user/reminders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteUserReminder: (id: number) =>
    request<void>(`/user/reminders/${id}`, { method: 'DELETE' }),
};

export type DashboardPayload = {
  today: {
    date: string;
    block: 'A' | 'B' | 'C';
    week_number: number;
    day_of_week: string;
    current_weight_kg: number | null;
    target_weight_today_kg: number | null;
    weight_delta_kg: number | null;
    days_to_deadline: number;
    kg_to_target: number | null;
    kg_to_buffer: number | null;
  };
  pace: {
    status: 'on_pace' | 'slow' | 'off_pace' | 'no_data';
    trailing_4wk_avg_loss_kg: number | null;
    expected_loss_per_week_kg: number;
    delta: number | null;
    // null = the server has no basis for an estimate (not enough weigh-ins).
    // It is never a number the UI may round, default, or render as 0.
    estimated_probability_pct: number | null;
  };
  today_checklist: {
    meals: { id: 'breakfast' | 'lunch' | 'dinner'; label: string; scheduled_time: string; completed: boolean }[];
    training: { session_id: string; completed: boolean };
    steps: { current: number; target: number };
    sleep_last_night_hours: number | null;
    creatine_taken: boolean;
    magnesium_taken: boolean;
    mobility_completed: boolean;
    water_liters: number | null;
    alcohol_consumed: boolean;
  };
  this_week: {
    week_number: number;
    monday_date: string;
    weight_logged: boolean;
    waist_logged: boolean;
    photos_taken: boolean;
    target_weight_kg: number;
    current_weight_kg: number | null;
    delta_from_target_kg: number | null;
    compliance_score_pct: number | null;
    off_plan_kcal_total: number;
  };
  current_block: {
    id: 'A' | 'B' | 'C';
    calorie_target: number;
    protein_target_g: number;
    step_target: number;
    weeks_remaining_in_block: number;
  };
  adherence: {
    tier: 'crushing' | 'nudge' | 'warning' | 'locked' | 'coach' | 'no_data';
    message: string;
    latest_compliance_pct: number | null;
  };
  creatine_masking?: {
    active: boolean;
    load_started_at: string;
    day_of_load: number;
    masking_days: number;
    days_remaining: number;
  };
  eating_window: {
    start: string; // 'HH:MM' user-local
    end: string;   // 'HH:MM' user-local
  };
};

export type PhotoMeta = {
  id: number;
  log_date: string;
  angle: 'front' | 'side' | 'back';
  bytes: number;
  content_type: string;
  created_at: string;
  url: string;
};

export type ComplianceBreakdown = {
  meals_pct: number;
  steps_pct: number;
  training_pct: number;
  sleep_pct: number;
  mobility_pct: number;
  alcohol_pct: number;
  total_pct: number;
};

export type WeeklyReview = {
  week_number: number;
  monday_date: string;
  block: 'A' | 'B' | 'C';
  weight_kg: number | null;
  target_weight_kg: number;
  delta_from_target_kg: number | null;
  trailing_4wk_avg_loss_kg: number | null;
  compliance_score_pct: number;
  compliance_breakdown?: ComplianceBreakdown;
  course_correction_action: string;
  action_message?: string;
  user_acknowledged_at: string | null;
  user_notes: string | null;
};

export type InsuranceItem = {
  id: string;
  label: string;
  detail: string;
  cost_per_month: string;
  priority: 'critical' | 'high' | 'medium' | 'optional';
  probability_hint?: string;
  active: boolean;
  activated_at: string | null;
  notes: string | null;
};

export type ProbabilityLadderRow = { label: string; pct: number };

export async function uploadPhoto(file: File, angle: 'front' | 'side' | 'back', date?: string) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('angle', angle);
  if (date) fd.append('date', date);
  const res = await fetch('/api/v1/photos', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new APIError(res.status, body);
  }
  return res.json() as Promise<{ id: number; angle: string; log_date: string; url: string; bytes: number }>;
}
